import { useEffect, useState } from "react";
import Modal, { ModalBody, ModalFooter } from "@components/ui/Modal";
import Button from "@components/ui/Button";
import {
  createCampaign, fetchProviders, fetchSettings, fetchTreatments,
} from "@services/reactivation";
import { SKIP_REASONS } from "@components/scheduling/campaignLabels";
import { describeProtocol } from "@components/scheduling/protocolText";
import { canUseTransferCampaigns } from "@utils/access";

const inputClass =
  "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
const labelClass = "block text-xs font-medium text-gray-600 mb-1";

// Mirrors MAX_CAMPAIGN_PATIENTS on the backend.
const MAX_PATIENTS = 1000;
const DEFAULT_RANDOM_COUNT = 20;

/** The whole number a count field holds, or null when it is blank or not whole. */
function parseCount(text) {
  if (!/^\d+$/.test(text)) return null;
  return Number(text);
}

/**
 * Turn a selection from the call list into a campaign, or in random mode
 * ask the backend to draw `patientCount` patients from the call list itself:
 * from the list `list` names (its key; `listTitle` is what the dialog calls
 * it), or, with none, from cancellations plus dormant.
 *
 * The clinic's own defaults decide the calling hours and whether a booking
 * confirmation is texted, so this asks only for what changes per campaign.
 * After a successful start it reports which patients the backend refused, or
 * in random mode how many it could enroll when fewer were eligible.
 *
 * A clinic an admin has cleared for transfer campaigns also chooses how a
 * patient is got in: booked through the PMS, or handed to the clinic's own
 * line. Only then are the pitch and the daily cap offered; for everyone else
 * the campaign books, as it always has, and the request carries none of the
 * three keys. `hasScheduling` says whether the clinic has a PMS at all:
 * without one the only mode that can work is transfer.
 */
export default function StartCampaignModal({
  show, patients, clinicId, onClose, onStarted, mode = "manual", eligibleCount = 0,
  list, listTitle,
  hasScheduling = true,
  transferAllowed = canUseTransferCampaigns(),
}) {
  const [treatments, setTreatments] = useState([]);
  const [treatmentId, setTreatmentId] = useState("");
  const [providers, setProviders] = useState([]);
  const [providerId, setProviderId] = useState("");
  const [name, setName] = useState("");
  const [countText, setCountText] = useState("");
  const [campaignMode, setCampaignMode] = useState("book");
  const [pitch, setPitch] = useState("");
  const [capText, setCapText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [skipped, setSkipped] = useState(null);
  const [shortfall, setShortfall] = useState(null);
  const [protocol, setProtocol] = useState(null);

  const random = mode === "random";
  // No PMS and no way to transfer: nothing this dialog could start would run.
  const noWayToBook = !hasScheduling && !transferAllowed;
  const transfer = transferAllowed && campaignMode === "transfer";
  // Providers are a PMS concept: a clinic without scheduling has none to list
  // (the request answers 400), and a transfer campaign sends none.
  const providersApply = hasScheduling && !transfer;
  const dailyCap = capText === "" ? null : parseCount(capText);
  const capValid = capText === "" || (dailyCap !== null && dailyCap >= 1);
  const maxCount = Math.min(eligibleCount, MAX_PATIENTS);
  const patientCount = parseCount(countText);
  const countValid =
    !random || (patientCount !== null && patientCount >= 1 && patientCount <= maxCount);
  // The call list can hold more than one campaign may take, so say so rather
  // than letting the capped `max` silently disagree with the eligible count.
  const capNote = eligibleCount > MAX_PATIENTS
    ? ` You can start up to ${MAX_PATIENTS} at a time.`
    : "";
  const plural = eligibleCount === 1 ? "" : "s";
  const where = listTitle ? "this list" : "the call list";
  const countHelp = countValid
    ? `Picked at random from the ${eligibleCount} eligible patient${plural} on ${where}.${capNote}`
    : `Enter a whole number between 1 and ${maxCount}.`;

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setError(null);
    setSkipped(null);
    setShortfall(null);
    setTreatmentId("");
    setName("");
    setPitch("");
    setCapText("");
    // The procedures belong to whichever clinic is selected now. Leaving the
    // previous clinic's on screen while these load, or after they fail, offers
    // a treatment id this clinic does not own.
    setTreatments([]);
    (async () => {
      try {
        const rows = await fetchTreatments(clinicId);
        if (!cancelled) setTreatments(rows);
      } catch (e) {
        if (!cancelled) {
          setTreatments([]);
          setError(e.message || "Could not load the clinic's procedures");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [show, clinicId, mode]);

  // The mode follows the clinic: one with no PMS can only transfer.
  useEffect(() => {
    if (!show) return;
    setCampaignMode(hasScheduling ? "book" : "transfer");
  }, [show, clinicId, hasScheduling]);

  // Seeded separately from the procedures load: the default count depends on
  // maxCount, and re-fetching the clinic's procedures because the call list
  // grew would throw away a treatment the user had already chosen.
  useEffect(() => {
    if (!show) return;
    setCountText(String(Math.max(1, Math.min(DEFAULT_RANDOM_COUNT, maxCount))));
  }, [show, maxCount]);

  useEffect(() => {
    if (!show) return undefined;
    // This effect never resets `error` itself: it relies on the procedures
    // effect above owning that `setError(null)` on open, so that effect must
    // stay registered before this one or a providers error from the previous
    // clinic would survive a switch.
    let cancelled = false;
    setProviderId("");
    // Same rule as the procedures: a provider id belongs to one clinic, so the
    // previous clinic's list must not stay selectable while this one loads.
    setProviders([]);
    if (!providersApply) return undefined;
    (async () => {
      try {
        const rows = await fetchProviders(clinicId);
        if (!cancelled) setProviders(rows);
      } catch (e) {
        if (!cancelled) {
          setProviders([]);
          setError(e.message || "Could not load the clinic's providers");
        }
      }
    })();
    return () => { cancelled = true; };
  }, [show, clinicId, providersApply]);

  useEffect(() => {
    if (!show) return undefined;
    let cancelled = false;
    setProtocol(null);
    // A settings failure describes nothing worse than the generic sentence
    // already covers, so it must never block starting a campaign.
    (async () => {
      try {
        const settings = await fetchSettings(clinicId);
        if (!cancelled) setProtocol(settings);
      } catch {
        if (!cancelled) setProtocol(null);
      }
    })();
    return () => { cancelled = true; };
  }, [show, clinicId]);

  const handleSubmit = async () => {
    if (!treatmentId) {
      setError("Choose what to offer these patients.");
      return;
    }
    if (!countValid || !capValid || noWayToBook) return;
    setSubmitting(true);
    setError(null);
    try {
      const { campaign, skipped: refused, requested, enrolled } = await createCampaign({
        clinicId,
        treatmentId,
        name: name.trim() || undefined,
        // The list the draw comes from; ticked rows carry their own.
        ...(random ? { patientCount, ...(list ? { list } : {}) } : { patients }),
        // Providers are a PMS concept; the backend refuses one in transfer mode.
        providerExternalId: transfer ? undefined : providerId || undefined,
        // Book is the server's default, so only a transfer is worth saying.
        ...(transfer ? { mode: "transfer" } : {}),
        ...(transferAllowed && pitch.trim() ? { pitch: pitch.trim() } : {}),
        ...(transferAllowed && dailyCap !== null ? { dailyNewContactCap: dailyCap } : {}),
      });
      // The page always learns the campaign started; the dialog stays open only
      // to report which patients the backend refused, or that it could enroll
      // fewer than were asked for.
      const short = random && enrolled !== null && requested !== null && enrolled < requested;
      onStarted(campaign, { keepOpen: refused.length > 0 || short });
      if (refused.length > 0) setSkipped(refused);
      if (short) setShortfall({ requested, enrolled });
    } catch (e) {
      setError(e.message || "Could not start the campaign");
    } finally {
      setSubmitting(false);
    }
  };

  const count = patients?.length || 0;

  return (
    <Modal show={show} onClose={onClose} title="Start a calling campaign" size="md">
      <ModalBody>
        {/* shortfall and skipped never coexist: the backend sends skipped=[] in random mode. */}
        {shortfall ? (
          <p className="text-sm text-gray-700">
            The campaign has started with {shortfall.enrolled} of the {shortfall.requested}{" "}
            patients you asked for. The rest of the call list could not be added right now:
            those patients are already in a campaign, still inside their cooldown period,
            have asked not to be called, or have no phone number on file.
          </p>
        ) : skipped ? (
          <div className="space-y-3">
            <p className="text-sm text-gray-700">
              The campaign has started. {skipped.length} patient
              {skipped.length === 1 ? " was" : "s were"} not added:
            </p>
            <ul className="divide-y divide-gray-200 rounded-lg border border-gray-200">
              {skipped.map((row) => (
                <li key={row.external_patient_id}
                    className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="text-gray-800">{row.name || row.external_patient_id}</span>
                  <span className="text-gray-500">
                    {SKIP_REASONS[row.reason] || row.reason}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className="space-y-4">
            {random && listTitle && (
              <p className="text-sm text-gray-700">{`Random patients from: ${listTitle}`}</p>
            )}
            {random ? (
              <div>
                <label className={labelClass} htmlFor="campaign-count">
                  How many patients to call <span className="text-red-500">*</span>
                </label>
                <input id="campaign-count" type="number" inputMode="numeric"
                       className={inputClass} value={countText} min={1} max={maxCount}
                       aria-describedby="campaign-count-help" aria-invalid={!countValid}
                       onChange={(e) => setCountText(e.target.value)} />
                <p id="campaign-count-help"
                   className={`mt-1 text-xs ${countValid ? "text-gray-500" : "text-red-600"}`}>
                  {countHelp}
                </p>
              </div>
            ) : (
              <p className="text-sm text-gray-700">
                {count} patient{count === 1 ? "" : "s"} will be called one at a time.
              </p>
            )}
            <p className="text-sm text-gray-700">
              {protocol
                ? describeProtocol(protocol)
                : "Patients are called following the clinic's calling protocol in Settings."}
            </p>

            <div>
              <label className={labelClass} htmlFor="campaign-treatment">
                What to offer <span className="text-red-500">*</span>
              </label>
              <select id="campaign-treatment" className={inputClass} value={treatmentId}
                      onChange={(e) => setTreatmentId(e.target.value)}>
                <option value="">Choose a procedure</option>
                {treatments.map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.display_name} ({t.default_duration_minutes} min)
                  </option>
                ))}
              </select>
              {treatments.length === 0 && (
                <p className="mt-2 text-sm text-amber-700">
                  No procedures set up for this clinic yet. Add one in Settings first.
                </p>
              )}
            </div>

            {noWayToBook && (
              <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">
                Campaigns need a scheduling integration to book appointments. Ask Klinifai
                to connect this clinic's scheduling system, or to enable transfer campaigns.
              </p>
            )}

            {transferAllowed && (
              <div>
                <label className={labelClass} htmlFor="campaign-mode">How to book</label>
                <select id="campaign-mode" className={inputClass} value={campaignMode}
                        onChange={(e) => setCampaignMode(e.target.value)}>
                  <option value="book" disabled={!hasScheduling}>Book via PMS</option>
                  <option value="transfer">Transfer to clinic</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {!hasScheduling
                    ? "This clinic has no scheduling integration, so interested patients are handed to the clinic's own line."
                    : transfer
                      ? "Interested patients are handed to the clinic's own line; the clinic books them."
                      : "The assistant books the appointment in the clinic's scheduling system."}
                </p>
              </div>
            )}

            {providersApply && providers.length > 0 && (
              <div>
                <label className={labelClass} htmlFor="campaign-provider">Book with</label>
                <select id="campaign-provider" className={inputClass} value={providerId}
                        onChange={(e) => setProviderId(e.target.value)}>
                  <option value="">Patient&apos;s own dentist</option>
                  {providers.map((p) => (
                    <option key={p.external_provider_id} value={p.external_provider_id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {transferAllowed && (
              <>
                <div>
                  <label className={labelClass} htmlFor="campaign-pitch">What to say</label>
                  <textarea id="campaign-pitch" rows={3} className={inputClass} value={pitch}
                            placeholder="Optional. The offer the assistant makes, e.g. a free checkup and a whitening kit for returning patients."
                            onChange={(e) => setPitch(e.target.value)} />
                </div>
                <div>
                  <label className={labelClass} htmlFor="campaign-cap">
                    New patients a day
                  </label>
                  <input id="campaign-cap" type="number" inputMode="numeric" min={1} step={1}
                         className={inputClass} value={capText}
                         placeholder="Optional, e.g. 200"
                         aria-describedby="campaign-cap-help" aria-invalid={!capValid}
                         onChange={(e) => setCapText(e.target.value)} />
                  <p id="campaign-cap-help"
                     className={`mt-1 text-xs ${capValid ? "text-gray-500" : "text-red-600"}`}>
                    {capValid
                      ? "How many patients are called for the first time each day. Follow-up calls are not counted. Leave empty for no limit."
                      : "Enter a whole number of at least 1, or leave it empty."}
                  </p>
                </div>
              </>
            )}

            <div>
              <label className={labelClass} htmlFor="campaign-name">Campaign name</label>
              <input id="campaign-name" className={inputClass} value={name}
                     placeholder="Optional, e.g. Q4 recall"
                     onChange={(e) => setName(e.target.value)} />
            </div>
          </div>
        )}

        {error && (
          <p className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>
        )}
      </ModalBody>
      <ModalFooter>
        {skipped || shortfall ? (
          <Button color="primary" onClick={onClose}>Done</Button>
        ) : (
          <>
            <Button color="light" onClick={onClose} disabled={submitting}>Cancel</Button>
            <Button color="primary" onClick={handleSubmit}
                    disabled={submitting || treatments.length === 0 || !countValid
                              || !capValid || noWayToBook}>
              {submitting ? "Starting…" : "Start calling"}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
}
